// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Dimensions drawn between two points.
 *
 * The reason these matter is not that a plan needs more numbers on it. It is
 * that a drawing arrives with dimensions already printed on it by whoever
 * surveyed the site, and the only way to know whether this app's idea of the
 * scale matches theirs is to measure one of them and compare. A dimension
 * across a property line labelled 234.50' that reads 231' is telling you the
 * scale is wrong before anything gets sited against it.
 *
 * So a dimension always shows what it measures. There is deliberately no way
 * to type a value onto one: a dimension that says what someone wishes were
 * true is worse than no dimension, because it looks like evidence.
 *
 * Everything here works in pixels. The plan stores points as percentages of
 * width and of height independently, which on a canvas that is not square
 * means a naive distance is wrong by the aspect ratio; converting first and
 * measuring here keeps that mistake out of the geometry.
 */

export interface Point { x: number; y: number }

export interface DimGeometry {
  /** The two points being measured. */
  a: Point; b: Point;
  /** The dimension line itself, pushed off the measured points. */
  da: Point; db: Point;
  /** Where the text sits. */
  mid: Point;
  /** How the text is turned, never past upright. */
  angleDeg: number;
  /** The unit normal the offset was applied along. */
  normal: Point;
  /** Distance between the two points, in pixels. */
  lengthPx: number;
}

/**
 * Where every part of a dimension goes.
 *
 * The offset pushes the dimension line clear of whatever is being measured,
 * which is what makes it readable over a drawing that already has lines
 * where the measurement is.
 */
export function dimGeometry(a: Point, b: Point, offsetPx = 0): DimGeometry | null {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  // Two points in the same place have no direction, so there is nothing to
  // draw and no angle to draw it at.
  if (!Number.isFinite(len) || len < 0.0001) return null;

  const normal = { x: -dy / len, y: dx / len };
  const off = Number.isFinite(offsetPx) ? offsetPx : 0;
  const da = { x: a.x + normal.x * off, y: a.y + normal.y * off };
  const db = { x: b.x + normal.x * off, y: b.y + normal.y * off };

  // atan2 gives (-180, 180]. Text that would be read upside down is folded
  // back the other way; the dimension still runs the direction it was drawn,
  // only the reading changes. Adding 180 unconditionally was wrong at the
  // ends of the range -- a right-to-left dimension came out at 360 degrees,
  // which is upright by luck and out of range by arithmetic.
  let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  if (angleDeg > 90) angleDeg -= 180;
  else if (angleDeg <= -90) angleDeg += 180;

  return {
    a, b, da, db,
    mid: { x: (da.x + db.x) / 2, y: (da.y + db.y) / 2 },
    angleDeg, normal, lengthPx: len,
  };
}

/**
 * The extension lines, which run from the measured points out past the
 * dimension line.
 *
 * They start a little away from the point itself, the way they are drawn by
 * hand and by every CAD package: a line that touches the corner it measures
 * hides the corner.
 */
export function extensionLines(g: DimGeometry | null, gap = 3, overshoot = 4):
Array<{ from: Point; to: Point }> {
  if (!g) return [];
  const off = Math.hypot(g.da.x - g.a.x, g.da.y - g.a.y);
  // No offset means the dimension line sits on the points and there is
  // nothing for an extension line to span.
  if (off <= gap) return [];
  const sign = Math.sign((g.da.x - g.a.x) * g.normal.x + (g.da.y - g.a.y) * g.normal.y) || 1;
  const unit = { x: g.normal.x * sign, y: g.normal.y * sign };
  const one = (p: Point) => ({
    from: { x: p.x + unit.x * gap, y: p.y + unit.y * gap },
    to: { x: p.x + unit.x * (off + overshoot), y: p.y + unit.y * (off + overshoot) },
  });
  return [one(g.a), one(g.b)];
}

/**
 * Feet and inches, the way a dimension is written on a drawing.
 *
 * 234.5 feet is "234'-6"", not "234.5 ft". Anyone checking this against a
 * surveyor's sheet is reading feet and inches, and a decimal makes them do
 * the conversion in their head to find out whether the two agree.
 */
export function formatFeetInches(feet: number, precision: "inch" | "half" | "decimal" = "inch"): string {
  if (!Number.isFinite(feet)) return "";
  const neg = feet < 0;
  const v = Math.abs(feet);

  if (precision === "decimal") return `${neg ? "-" : ""}${v.toFixed(2)}'`;

  const step = precision === "half" ? 0.5 : 1;
  let whole = Math.floor(v);
  let inches = Math.round(((v - whole) * 12) / step) * step;
  // Rounding up to twelve inches is another foot, not a twelfth of one.
  if (inches >= 12) { whole += 1; inches -= 12; }

  const inchText = inches === 0 ? "" : `-${inches % 1 === 0 ? inches : inches.toFixed(1)}"`;
  return `${neg ? "-" : ""}${whole}'${inchText}`;
}

/** What the dimension reads, or nothing when the plan has no scale. */
export function dimensionText(lengthPx: number, feetPerPixel: number | null | undefined): string {
  if (!feetPerPixel || !Number.isFinite(feetPerPixel) || feetPerPixel <= 0) return "";
  return formatFeetInches(lengthPx * feetPerPixel);
}
